import DOMPurify from 'dompurify';
import parseHTML from 'html-react-parser';

// LaTeX delimiter patterns: support 1 or 2 backslashes so CMS/API double-escape still matches
const BS = '\\\\';
const DISPLAY_BRACKET_OPEN = `(?:${BS}{1,2})\\[`;
const DISPLAY_BRACKET_CLOSE = `(?:${BS}{1,2})\\]`;
const INLINE_PAREN_OPEN = `(?:${BS}{1,2})\\(`;
const INLINE_PAREN_CLOSE = `(?:${BS}{1,2})\\)`;
const ENV_BEGIN = `(?:${BS}{1,2})begin`;
const ENV_END = `(?:${BS}{1,2})end`;
const ENV_NAME = '[a-zA-Z0-9*]+';

/** Strip HTML tags from a string (e.g. inside extracted LaTeX so MathJax gets clean input). */
const stripHtmlFromMath = (s: string) => s.replace(/<[^>]{0,500}>/g, '');

const RE_BEGIN_ENV_TAGS = /\\begin\{(?:<[^>]+>)*([a-zA-Z0-9*]+)(?:<\/[^>]+>)*\}/g;
const RE_END_ENV_TAGS = /\\end\{(?:<[^>]+>)*([a-zA-Z0-9*]+)(?:<\/[^>]+>)*\}/g;

function pushMathBlock(mathBlocks: { placeholder: string; content: string }[], mathIndex: number, prefix: string, match: string): string {
    const placeholder = `__MATHJAX_${prefix}_${mathIndex}__`;
    mathBlocks.push({ placeholder, content: stripHtmlFromMath(match) });
    return placeholder;
}

export const unwrapParagraphWrappedTables = (html: string): string => html.replace(/<p\b[^>]*>\s*(<table\b[\s\S]*?<\/table>)\s*<\/p>/gi, '$1');

/**
 * Convert Quill formula embeds to MathJax inline delimiters.
 * Quill serialises as <span class="ql-formula" data-value="e=mc^2">…</span>.
 */
export const convertQuillFormulaToLatex = (html: string): string => {
    const decodeAttr = (latex: string) =>
        latex
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"');

    const openTagRe = /<span\b(?=[^>]*\bclass\s*=\s*["'][^"']*\bql-formula\b)(?=[^>]*\bdata-value\s*=\s*["']([^"']*)["'])[^>]*>/gi;

    let result = '';
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = openTagRe.exec(html)) !== null) {
        const latex = decodeAttr(match[1]);
        const openEnd = match.index + match[0].length;
        let depth = 1;
        let i = openEnd;
        while (i < html.length && depth > 0) {
            const nextOpen = html.indexOf('<span', i);
            const nextClose = html.indexOf('</span>', i);
            if (nextClose === -1) break;
            if (nextOpen !== -1 && nextOpen < nextClose) {
                depth += 1;
                i = nextOpen + 5;
            } else {
                depth -= 1;
                i = nextClose + 7;
                if (depth === 0) {
                    result += html.slice(lastIndex, match.index) + `\\(${latex}\\)`;
                    lastIndex = i;
                    openTagRe.lastIndex = i;
                }
            }
        }
        if (depth !== 0) {
            break;
        }
    }

    return result + html.slice(lastIndex);
};

/**
 * Sanitize quiz HTML and preserve LaTeX for MathJax (ported from jove.com-ui parse()).
 */
export const parseQuizHtml = (str = ''): ReturnType<typeof parseHTML> | string => {
    try {
        const normalizedStr = convertQuillFormulaToLatex(str)
            .replace(RE_BEGIN_ENV_TAGS, '\\begin{$1}')
            .replace(RE_END_ENV_TAGS, '\\end{$1}')
            .replace(/&amp;/g, '&')
            .replace(/<span>\\+<\/span>/g, '\\\\');

        const mathBlocks: { placeholder: string; content: string }[] = [];
        let mathIndex = 0;

        let processedStr = normalizedStr.replace(new RegExp(`(${ENV_BEGIN}\\{${ENV_NAME}\\}[\\s\\S]*?${ENV_END}\\{${ENV_NAME}\\})`, 'g'), (m) => {
            const placeholder = pushMathBlock(mathBlocks, mathIndex, 'ENV', m);
            mathIndex += 1;
            return placeholder;
        });

        processedStr = processedStr.replace(
            new RegExp(`(\\$\\$[\\s\\S]*?\\$\\$)|(${DISPLAY_BRACKET_OPEN}[\\s\\S]*?${DISPLAY_BRACKET_CLOSE})`, 'g'),
            (m) => {
                const placeholder = pushMathBlock(mathBlocks, mathIndex, 'DISPLAY', m);
                mathIndex += 1;
                return placeholder;
            }
        );

        processedStr = processedStr.replace(new RegExp(`(${INLINE_PAREN_OPEN}[\\s\\S]*?${INLINE_PAREN_CLOSE})`, 'g'), (m) => {
            const placeholder = pushMathBlock(mathBlocks, mathIndex, 'INLINE', m);
            mathIndex += 1;
            return placeholder;
        });

        const cleanedStr = processedStr.replace(/<\s*mark\b[^>]*>/gi, '&lt;mark&gt;').replace(/<\s*\/\s*mark\s*>/gi, '&lt;/mark&gt;');
        const sanitizedHTML = DOMPurify.sanitize(cleanedStr, {
            ADD_ATTR: ['target', 'rel'],
        });

        let finalHTML = sanitizedHTML;
        mathBlocks.forEach(({ placeholder, content }) => {
            finalHTML = finalHTML.replace(placeholder, () => content);
        });

        finalHTML = unwrapParagraphWrappedTables(finalHTML);

        return parseHTML(finalHTML);
    } catch {
        return 'Unable to parse HTML';
    }
};
